import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import { reactSelectTokenStyles } from '../../styles/reactSelectTokenStyles';

interface RelatedOfferSelectorProps {
  onSelectOffer: (offerId: string, offerNumber: string, offerData?: any) => void;
  selectedOfferId: string | null;
}

interface OfferOption {
  value: string;
  label: string;
  data: any;
}

const RelatedOfferSelector: React.FC<RelatedOfferSelectorProps> = ({ onSelectOffer, selectedOfferId }) => {
  const { t, i18n } = useTranslation('invoice');
  const [offers, setOffers] = useState<OfferOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, effectiveScopeUid } = useAuth();

  useEffect(() => {
    const fetchOffers = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const offersRef = collection(db, 'offers');
        
        // Nur Angebote mit nutzbaren Status filtern
        // Angebote mit Status "rejected", "expired" werden herausgefiltert
        // "created", "draft", "sent", "accepted" und "revised" sind relevante Status
        const q = query(
          offersRef,
          where('userId', '==', effectiveScopeUid),
          where('status', 'in', ['created', 'draft', 'sent', 'accepted', 'revised']),
          orderBy('offerNumber', 'desc') // Nach Angebotsnummer absteigend sortieren (neuer zu alt)
        );

        const snapshot = await getDocs(q);
        let offerOptions: OfferOption[] = [];

        console.log(`Anzahl gefundener Angebote: ${snapshot.size}`);

        // Debugging: Details der gefundenen Angebote ausgeben
        snapshot.forEach(doc => {
          const offerData = doc.data();
          console.log(`Angebot gefunden: ID=${doc.id}, Nummer=${offerData.offerNumber}, Status=${offerData.status}`);
          
          // Versuchen alle möglichen Felder zu prüfen, die auf eine bereits erstellte Rechnung hinweisen könnten
          // Diese Felder werden jetzt über den offerService.markOfferAsInvoiced() gesetzt,
          // wenn eine Rechnung basierend auf einem Angebot erstellt wird
          const isAlreadyInvoiced = 
            (offerData.workflowStatus === 'invoiced') || 
            (offerData.invoiced === true) ||
            (offerData.hasInvoice === true) ||
            (offerData.status === 'invoiced');
            
          // Nur Angebote anzeigen, die nicht bereits in Rechnung gestellt wurden
          if (!isAlreadyInvoiced) {
            offerOptions.push({
              value: doc.id,
              label: `${offerData.offerNumber} - ${offerData.buyerName || 'Unbenannt'} (${formatDateCentral(offerData.offerDate, { locale: i18n.language })}) - ${getStatusText(offerData.status)}`,
              data: offerData
            });
          }
        });

        setOffers(offerOptions);
        console.log(`Anzahl der angezeigten Angebote: ${offerOptions.length}`);
      } catch (err) {
        console.error('Fehler beim Laden der Angebote:', err);
        setError('Angebote konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOffers();
  }, [currentUser, effectiveScopeUid]);
  
  // Hilfsfunktion, um den Status-Text benutzerfreundlich darzustellen
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'created': return t('status.created', { ns: 'offer' });
      case 'draft': return t('status.draft', { ns: 'offer' });
      case 'sent': return t('status.sent', { ns: 'offer' });
      case 'accepted': return t('status.accepted', { ns: 'offer' });
      case 'rejected': return t('status.rejected', { ns: 'offer' });
      case 'expired': return t('status.expired', { ns: 'offer' });
      case 'revised': return t('status.revised', { ns: 'offer' });
      case 'invoiced': return t('status.invoiced', { ns: 'offer' });
      default: return status;
    }
  };

  const handleOfferChange = (selectedOption: any) => {
    if (selectedOption) {
      console.log('Ausgewähltes Angebot in handleOfferChange:', selectedOption);
      console.log('Angebotsdaten die übergeben werden:', selectedOption.data);
      
      // Prüfen, ob items ein Array ist und loggen
      if (selectedOption.data && selectedOption.data.items) {
        console.log('Angebot enthält Items-Array:', Array.isArray(selectedOption.data.items), 'Länge:', selectedOption.data.items.length);
        
        // Logge die ersten paar Items für Debugging-Zwecke
        if (Array.isArray(selectedOption.data.items) && selectedOption.data.items.length > 0) {
          console.log('Erstes Item im Angebot:', selectedOption.data.items[0]);
        }
      } else {
        console.log('Angebot enthält keine Items oder items ist kein Array:', selectedOption.data.items);
      }
      
      // Prüfen, ob buyer vorhanden ist
      if (selectedOption.data && selectedOption.data.buyer) {
        console.log('Buyer-Daten vorhanden:', selectedOption.data.buyer);
      } else if (selectedOption.data && selectedOption.data.buyerName) {
        console.log('Nur buyerName vorhanden:', selectedOption.data.buyerName);
      } else {
        console.log('Keine Buyer-Daten gefunden');
      }
      
      // Sicherstellen, dass wir gültige Daten übergeben
      const validData = {
        ...selectedOption.data,
        // Stelle sicher, dass items ein Array ist, wenn es existiert
        items: selectedOption.data.items && Array.isArray(selectedOption.data.items) 
              ? selectedOption.data.items 
              : [],
        // Stelle sicher, dass buyer ein Objekt ist, wenn es existiert
        buyer: selectedOption.data.buyer && typeof selectedOption.data.buyer === 'object'
              ? selectedOption.data.buyer
              : (selectedOption.data.buyerName 
                ? { name: selectedOption.data.buyerName }
                : undefined)
      };
      
      onSelectOffer(selectedOption.value, selectedOption.label.split(' - ')[0], validData);
    } else {
      console.log('Kein Angebot ausgewählt, setze zurück');
      onSelectOffer('', '', undefined);
    }
  };

  const selectedOption = offers.find(offer => offer.value === selectedOfferId) || null;

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-secondary mb-1">
        {t('offer.basedOn')}
      </label>
      <Select
        value={selectedOption}
        onChange={handleOfferChange}
        options={offers}
        isLoading={isLoading}
        isClearable
        placeholder={t('offer.selectOffer')}
        noOptionsMessage={() => t('noOffersFound', { ns: 'offer' }) || "Keine Angebote gefunden"}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={{
          control: (base, state) => ({
            ...(reactSelectTokenStyles.control?.(base, state) ?? base),
            minHeight: '48px', // Noch größer für mobile
            fontSize: '16px', // Verhindert Zoom auf iOS
          }),
          menu: (base, state) => ({
            ...(reactSelectTokenStyles.menu?.(base, state) ?? base),
            zIndex: 9999,
            fontSize: '16px', // Größere Schrift im Menü
          }),
          option: (base, state) => ({
            ...(reactSelectTokenStyles.option?.(base, state) ?? base),
            padding: '14px 16px', // Noch mehr Padding
            fontSize: '16px',
          }),
          singleValue: (base, state) => ({
            ...(reactSelectTokenStyles.singleValue?.(base, state) ?? base),
            fontSize: '16px',
          }),
          input: (base, state) => ({
            ...(reactSelectTokenStyles.input?.(base, state) ?? base),
            fontSize: '16px',
            margin: 0,
            paddingLeft: 0,
            paddingRight: 0,
          }),
          placeholder: (base, state) => ({
            ...(reactSelectTokenStyles.placeholder?.(base, state) ?? base),
            fontSize: '16px',
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '8px 12px',
          }),
          indicatorsContainer: (base) => ({
            ...base,
            padding: '8px',
          })
        }}
      />
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      {offers.length === 0 && !isLoading && !error && 
        <p className="mt-1 text-sm text-yellow-500">{t('noOffersFound', { ns: 'offer' })}</p>
      }
    </div>
  );
};

export default RelatedOfferSelector; 